import React, { useState } from 'react'
import { Col, Container, OverlayTrigger, Row, Spinner, Tooltip as BootstrapTooltip } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'

import { promptFileDownload } from '../../../common/fileUtils'

import { useDeleteFormTemplateByIdMutation, useGetAllFormTemplatesQuery } from '../../../store/ai/formTemplate'
import { useLazyDownloadGlobalFileRequestQuery } from '../../../store/media/file'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'

import { FormTemplate } from '../../../models/FormTemplate'
import { Scorecard } from '../../../models/Scorecard'

import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { Button } from '../../../components/Button/Button'
import { Modal } from '../../../components/Modal/Modal'

import { DELTA_TRIAL_FEATURES } from '../../Plan/DeltaTrial/DeltaTrialFeatures'
import { Upsell } from '../../Plan/DeltaTrial/Upsell/Upsell'

import { countFormFieldSourcesInDescriptor, countMappedFormFieldSources } from '../FormTemplates/utils'

interface FormTemplatesTabProps {
  scorecard: Scorecard | null
}

export const FormTemplatesTab: React.FC<FormTemplatesTabProps> = ({ scorecard }) => {
  const { isFetching: quotasFetching, hasQuota, quotaSuccess } = useZocksQuota()

  if (!quotaSuccess && quotasFetching) {
    return null
  }

  const documentSummaryEnabled = hasQuota('ai.documentSummaryAgent.value')

  if (!documentSummaryEnabled) {
    return <Upsell feature={DELTA_TRIAL_FEATURES['formTemplate']} />
  }

  return <FormTemplatesTabContent scorecard={scorecard} />
}

export const FormTemplatesTabContent: React.FC<FormTemplatesTabProps> = ({ scorecard }) => {
  const accountId = useAppSelector(selectAccountId)

  const navigate = useNavigate()
  const { formId = '' } = useParams()

  const [triggerDeleteFormTemplate, { isLoading: deleteLoading }] = useDeleteFormTemplateByIdMutation()
  const [triggerGetFileDownloadInfo] = useLazyDownloadGlobalFileRequestQuery()

  const { data: formTemplates, isFetching: formTemplatesLoading } = useGetAllFormTemplatesQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const [selectedFormTemplate, setSelectedFormTemplate] = useState<FormTemplate | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  const handleAddNewFormTemplate = () => {
    navigate(`/workspace/setup/forms/form/details/${formId}/templates/create`)
  }

  const handleShowFormTemplate = (formTemplate: FormTemplate) => {
    navigate(`/workspace/setup/forms/form/details/${formId}/templates/details/${formTemplate.id}`)
  }

  const handleDeleteFormTemplate = async (formTemplate: FormTemplate) => {
    try {
      const { id: formTemplateId } = formTemplate
      await triggerDeleteFormTemplate({ accountId, formTemplateId })
    } catch (e) {
      console.error(e)
    } finally {
      handleHideDeleteModal()
    }
  }

  const handleShowDeleteModal = (formTemplate: FormTemplate) => {
    setSelectedFormTemplate(formTemplate)
    setShowDeleteModal(true)
  }

  const handleHideDeleteModal = () => {
    setShowDeleteModal(false)
    setSelectedFormTemplate(null)
  }

  const handleDownloadFormTemplateFile = async (fileId: string) => {
    try {
      const { data: fileDownloadInfoResponse } = await triggerGetFileDownloadInfo({ accountId, fileId })
      if (!fileDownloadInfoResponse) {
        return
      }
      const { data: fileDownloadInfo } = fileDownloadInfoResponse
      const { location, fileInfo } = fileDownloadInfo
      const response = await fetch(location, { method: 'GET' })
      const fileContent = await response.blob()
      await promptFileDownload(fileContent, fileInfo.fileName)
    } catch (e) {
      console.error(e)
    }
  }

  if (formTemplatesLoading) {
    return <Spinner />
  }

  return (
    <>
      {showDeleteModal && selectedFormTemplate ? (
        <DeleteFormTemplateConfirmationModal
          show={showDeleteModal}
          onConfirm={() => handleDeleteFormTemplate(selectedFormTemplate)}
          onHide={handleHideDeleteModal}
          confirmLoading={deleteLoading}
        />
      ) : null}
      <Container fluid>
        <Row>
          <Col className='p-0'>
            {!formTemplates?.data.length ? (
              <div className='d-flex flex-column gap-3 align-items-center p-3'>
                <div className='d-flex flex-column gap-2 align-items-center form-template-list-no-templates-textbox'>
                  <h5>Skip manual document filling</h5>
                  <span className='paragraph-large text-center'>Upload your branded PDF templates and Zocks will automatically fill and generate them.</span>
                </div>
                <ScopedBlock
                  missingScopeAction={MissingScopeAction.DISABLE}
                  scope={{
                    scope: 'form_template:create',
                    superScope: ['form_template:admin'],
                  }}
                >
                  <Button variant='primary' className='px-3 py-2 button-medium' onClick={handleAddNewFormTemplate}>
                    Upload your first template
                  </Button>
                </ScopedBlock>
              </div>
            ) : (
              <div className='h-100 w-100 d-flex flex-column zocks-gap-2 form-template-update-container'>
                <div className='d-flex align-items-center justify-content-between'>
                  <div className='d-flex flex-column gap-1'>
                    <h5>Document templates</h5>
                    <span>Add branded PDF templates to automate filling them with form data.</span>
                  </div>
                  <ScopedBlock
                    missingScopeAction={MissingScopeAction.DISABLE}
                    scope={{
                      scope: 'form_template:create',
                      superScope: ['form_template:admin'],
                    }}
                  >
                    <Button variant='grayGhost' className='px-3 py-2 button-medium' onClick={handleAddNewFormTemplate}>
                      Upload template
                    </Button>
                  </ScopedBlock>
                </div>
                <div className='d-flex flex-column'>
                  <div className='d-flex align-items-center rounded-top-4 zocks-bg-color-gray-200'>
                    <span className='w-50 px-3 zocks-py-0.75 lead-extra-small zocks-text-gray-700'>Template file</span>
                    <span className='w-50 px-3 zocks-py-0.75 lead-extra-small zocks-text-gray-700'>Matched Form fields</span>
                    <div className='form-template-list-spacer flex-shrink-0' />
                  </div>
                  <div className='d-flex flex-column border rounded-bottom-4 zocks-border-color-gray-200'>
                    {formTemplates.data.map((formTemplate) => {
                      const totalFormFieldSourceCount = scorecard ? countFormFieldSourcesInDescriptor(scorecard.descriptor) : 0
                      const mappedFormFieldSourceCount = countMappedFormFieldSources(formTemplate.mapping)

                      return (
                        <div className='d-flex align-items-center border-top zocks-border-color-gray-200'>
                          <div className='w-50 d-flex align-items-center px-3 zocks-py-0.75 gap-2'>
                            <span className='paragraph-small'>{formTemplate.name}</span>
                          </div>
                          <div className='w-50 d-flex flex-column px-3 zocks-py-0.75'>
                            <span className='paragraph-small'>
                              {mappedFormFieldSourceCount} of {totalFormFieldSourceCount}
                            </span>
                          </div>
                          <div className='d-flex align-items-center gap-1 p-3'>
                            <ScopedBlock
                              missingScopeAction={MissingScopeAction.DISABLE}
                              scope={{
                                scope: 'form_template:delete',
                                superScope: ['form_template:admin'],
                              }}
                            >
                              <OverlayTrigger
                                trigger={['hover', 'focus']}
                                placement='top'
                                overlay={<BootstrapTooltip className='zocks-text-gray-500'>Delete Document template</BootstrapTooltip>}
                              >
                                <Button
                                  className='form-template-list-icon p-0 d-flex align-items-center justify-content-center'
                                  onClick={() => handleShowDeleteModal(formTemplate)}
                                >
                                  <i className='bi bi-trash zocks-text-gray-700' />
                                </Button>
                              </OverlayTrigger>
                            </ScopedBlock>
                            <ScopedBlock
                              missingScopeAction={MissingScopeAction.DISABLE}
                              scope={{
                                scope: 'form_template:read',
                                superScope: ['form_template:admin'],
                              }}
                            >
                              <OverlayTrigger
                                trigger={['hover', 'focus']}
                                placement='top'
                                overlay={<BootstrapTooltip className='zocks-text-gray-500'>Download template file</BootstrapTooltip>}
                              >
                                <Button
                                  className='form-template-list-icon p-0 d-flex align-items-center justify-content-center'
                                  onClick={() => handleDownloadFormTemplateFile(formTemplate.fileId)}
                                >
                                  <i className='bi bi-download zocks-text-gray-700' />
                                </Button>
                              </OverlayTrigger>
                            </ScopedBlock>
                            <ScopedBlock
                              missingScopeAction={MissingScopeAction.DISABLE}
                              scope={{
                                scope: 'form_template:update',
                                superScope: ['form_template:admin'],
                              }}
                            >
                              <OverlayTrigger
                                trigger={['hover', 'focus']}
                                placement='top'
                                overlay={<BootstrapTooltip className='zocks-text-gray-500'>Edit mapping</BootstrapTooltip>}
                              >
                                <Button
                                  className='form-template-list-icon p-0 d-flex align-items-center justify-content-center'
                                  onClick={() => handleShowFormTemplate(formTemplate)}
                                >
                                  <i className='bi bi-pencil-square zocks-text-gray-700' />
                                </Button>
                              </OverlayTrigger>
                            </ScopedBlock>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </>
  )
}

export interface DeleteFormTemplateConfirmationModalProps {
  show: boolean
  onConfirm: () => void
  onHide: () => void
  confirmLoading: boolean
}

export const DeleteFormTemplateConfirmationModal: React.FC<DeleteFormTemplateConfirmationModalProps> = ({ show, onConfirm, onHide, confirmLoading }) => {
  return (
    <Modal
      show={show}
      onHide={onHide}
      header={<></>}
      body={
        <div className='d-flex flex-column zocks-gap-3'>
          <h4 className='text-center'>Are you sure you want to delete this Document Template?</h4>
        </div>
      }
      footer={
        <div className='d-flex flex-grow-1 gap-3 align-items-center'>
          <Button variant='secondary' className='flex-grow-1' onClick={onHide}>
            Cancel
          </Button>
          <Button variant='primary' className='flex-grow-1' onClick={onConfirm} loading={confirmLoading}>
            Delete
          </Button>
        </div>
      }
      centered={true}
    />
  )
}
